import React, { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

import "./UserMonthlyBonusHistory.css";

const UserMonthlyBonusHistory = ({ onBack }) => {
  const [username, setUsername] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedBonus, setSelectedBonus] = useState(null);

  // =========================================================
  // GET USERNAME FROM LOCAL STORAGE
  // =========================================================

  useEffect(() => {
    const getUsername = () => {
      try {
        const possibleKeys = [
          "username",
          "user",
          "currentUser",
          "userData",
          "loggedInUser",
        ];

        let foundUsername = "";

        for (const key of possibleKeys) {
          const value = localStorage.getItem(key);

          if (!value) continue;

          try {
            const parsed = JSON.parse(value);

            // Example:
            // localStorage.setItem("username", "Pigiment21")
            if (typeof parsed === "string") {
              foundUsername = parsed;
              break;
            }

            // Example:
            // localStorage.setItem(
            //   "userData",
            //   JSON.stringify({
            //      username: "Pigiment21"
            //   })
            // )
            if (
              parsed &&
              typeof parsed === "object" &&
              parsed.username
            ) {
              foundUsername = parsed.username;
              break;
            }
          } catch {
            // Plain text value
            foundUsername = value;
            break;
          }
        }

        setUsername(
          String(foundUsername || "").trim()
        );
      } catch (err) {
        console.error(
          "Failed to read username:",
          err
        );

        setUsername("");
      }
    };

    getUsername();

    // =======================================================
    // LISTEN FOR LOCAL STORAGE CHANGES
    // =======================================================

    const handleStorageChange = () => {
      getUsername();
    };

    window.addEventListener(
      "storage",
      handleStorageChange
    );

    return () => {
      window.removeEventListener(
        "storage",
        handleStorageChange
      );
    };
  }, []);

  // =========================================================
  // LOAD MONTHLY BONUS HISTORY
  // =========================================================

  useEffect(() => {
    if (!username) {
      setHistory([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    console.log(
      "Loading monthly bonus history for:",
      username
    );

    // =======================================================
    // FIRESTORE COLLECTION
    // =======================================================

    const q = query(
      collection(db, "monthly_bonuses"),
      where("username", "==", username)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // =====================================================
        // SORT NEWEST MONTH FIRST
        // =====================================================

        list.sort((a, b) => {
          const aYear = Number(a.year || 0);
          const bYear = Number(b.year || 0);

          if (bYear !== aYear) {
            return bYear - aYear;
          }

          const aMonth = Number(a.month || 0);
          const bMonth = Number(b.month || 0);

          if (bMonth !== aMonth) {
            return bMonth - aMonth;
          }

          const aTime =
            a.approvedAt?.seconds ||
            a.createdAt?.seconds ||
            a.updatedAt?.seconds ||
            0;

          const bTime =
            b.approvedAt?.seconds ||
            b.createdAt?.seconds ||
            b.updatedAt?.seconds ||
            0;

          return bTime - aTime;
        });

        console.log(
          "Monthly bonus history:",
          list
        );

        setHistory(list);
        setLoading(false);
      },
      (err) => {
        console.error(
          "Monthly bonus history error:",
          err
        );

        setError(
          "Failed to load monthly bonus history."
        );

        setHistory([]);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [username]);

  // =========================================================
  // FORMAT NUMBER
  // =========================================================

  const formatNumber = (value) => {
    return Number(value || 0).toLocaleString(
      "en-US"
    );
  };

  // =========================================================
  // MONTH NAME
  // =========================================================

  const getMonthName = (month, year) => {
    if (!month) {
      return year
        ? String(year)
        : "-";
    }

    const monthNumber = Number(month);

    const names = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const name = names[monthNumber - 1];

    if (!name) {
      return year
        ? `${month} ${year}`
        : String(month);
    }

    return year
      ? `${name} ${year}`
      : name;
  };

  // =========================================================
  // FORMAT DATE
  // =========================================================

  const formatDate = (timestamp) => {
    if (!timestamp) {
      return "-";
    }

    try {
      let date;

      if (
        typeof timestamp.toDate ===
        "function"
      ) {
        date = timestamp.toDate();
      } else if (timestamp.seconds) {
        date = new Date(
          timestamp.seconds * 1000
        );
      } else if (
        timestamp instanceof Date
      ) {
        date = timestamp;
      } else {
        return "-";
      }

      return date.toLocaleDateString(
        "en-GB",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      );
    } catch {
      return "-";
    }
  };

  // =========================================================
  // FORMAT DATE + TIME
  // =========================================================

  const formatDateTime = (timestamp) => {
    if (!timestamp) {
      return "-";
    }

    try {
      let date;

      if (
        typeof timestamp.toDate ===
        "function"
      ) {
        date = timestamp.toDate();
      } else if (timestamp.seconds) {
        date = new Date(
          timestamp.seconds * 1000
        );
      } else if (
        timestamp instanceof Date
      ) {
        date = timestamp;
      } else {
        return "-";
      }

      return date.toLocaleString(
        "en-GB",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }
      );
    } catch {
      return "-";
    }
  };

  // =========================================================
  // STATUS CLASS
  // =========================================================

  const getStatusClass = (status) => {
    const value = String(
      status || ""
    ).toLowerCase();

    if (
      value === "approved" ||
      value === "success" ||
      value === "paid"
    ) {
      return "history-status success";
    }

    if (
      value === "pending" ||
      value === "partial" ||
      value === "review"
    ) {
      return "history-status pending";
    }

    if (
      value === "rejected" ||
      value === "failed"
    ) {
      return "history-status danger";
    }

    return "history-status neutral";
  };

  // =========================================================
  // STATUS LABEL
  // =========================================================

  const getStatusLabel = (status) => {
    if (!status) {
      return "PENDING";
    }

    return String(status)
      .replace(/_/g, " ")
      .toUpperCase();
  };

  // =========================================================
  // BACK
  // =========================================================

  const handleBack = () => {
    if (typeof onBack === "function") {
      onBack();
      return;
    }

    window.history.back();
  };

  // =========================================================
  // CLOSE DETAILS
  // =========================================================

  const closeDetails = () => {
    setSelectedBonus(null);
  };

  // =========================================================
  // ESC KEY
  // =========================================================

  useEffect(() => {
    if (!selectedBonus) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeDetails();
      }
    };

    document.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      document.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [selectedBonus]);

  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <div className="user-monthly-history">

        {/* BACK BUTTON */}

        <button
          className="history-back-btn"
          onClick={handleBack}
          type="button"
        >
          <span className="back-arrow">
            ←
          </span>

          <span>
            Back
          </span>
        </button>

        {/* LOADING */}

        <div className="history-loading">

          <div className="history-spinner"></div>

          <p>
            Loading monthly bonus history...
          </p>

        </div>

      </div>
    );
  }

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="user-monthly-history">

      {/* =====================================================
          BACK BUTTON
      ===================================================== */}

      <button
        className="history-back-btn"
        onClick={handleBack}
        type="button"
      >
        <span className="back-arrow">
          ←
        </span>

        <span>
          Back
        </span>
      </button>

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="history-header">

        <div className="history-header-left">

          <span className="history-eyebrow">
            BONUS HISTORY
          </span>

          <h2>
            Monthly Bonus
          </h2>

          <p>
            View your monthly bonus history
            and performance details.
          </p>

          {username && (
            <div className="history-username">
              {username}
            </div>
          )}

        </div>

        {/* MONTH COUNT */}

        <div className="history-count">

          <span>
            MONTHS
          </span>

          <strong>
            {history.length}
          </strong>

        </div>

      </div>

      {/* =====================================================
          ERROR
      ===================================================== */}

      {error && (
        <div className="history-error">
          {error}
        </div>
      )}

      {/* =====================================================
          EMPTY
      ===================================================== */}

      {!error &&
      history.length === 0 ? (
        <div className="history-empty">

          <div className="history-empty-icon">
            📊
          </div>

          <h3>
            No Monthly Bonus History
          </h3>

          <p>
            Your monthly bonus records
            will appear here once available.
          </p>

        </div>
      ) : (

        /* ===================================================
           HISTORY LIST
        =================================================== */

        <div className="history-list">

          {history.map((item, index) => {

            // ===============================================
            // REAL FIRESTORE FIELDS
            // ===============================================

            const bonusAmount =
              Number(
                item.bonusAmount || 0
              );

            const status =
              item.status || "pending";

            const isApproved =
              String(status).toLowerCase() ===
              "approved";

            const monthName =
              getMonthName(
                item.month,
                item.year
              );

            const date =
              formatDate(
                item.approvedAt
              );

            return (
              <div
                className={`history-row ${
                  isApproved
                    ? "history-row-approved"
                    : ""
                }`}
                key={item.id}
              >

                {/* =========================================
                    MONTH
                ========================================= */}

                <div className="history-row-month">

                  <span>
                    {monthName}
                  </span>

                  <small>
                    {item.monthKey ||
                      item.year ||
                      "-"}
                  </small>

                </div>

                {/* =========================================
                    DATE
                ========================================= */}

                <div className="history-row-date">

                  <span>
                    DATE
                  </span>

                  <strong>
                    {date}
                  </strong>

                </div>

                {/* =========================================
                    AMOUNT
                ========================================= */}

                <div className="history-row-amount">

                  <span>
                    AMOUNT
                  </span>

                  <strong>
                    {formatNumber(
                      bonusAmount
                    )}{" "}
                    {item.currency ||
                      "TZS"}
                  </strong>

                </div>

                {/* =========================================
                    STATUS
                ========================================= */}

                <div className="history-row-status">

                  <span>
                    STATUS
                  </span>

                  <div
                    className={getStatusClass(
                      status
                    )}
                  >
                    {getStatusLabel(
                      status
                    )}
                  </div>

                </div>

                {/* =========================================
                    VIEW BUTTON
                ========================================= */}

                <button
                  type="button"
                  className="history-view-btn"
                  onClick={() =>
                    setSelectedBonus(item)
                  }
                  aria-label={`View ${monthName} details`}
                >

                  <span>
                    View
                  </span>

                  <span className="view-arrow">
                    →
                  </span>

                </button>

              </div>
            );
          })}

        </div>
      )}

      {/* =====================================================
          DETAILS MODAL
      ===================================================== */}

      {selectedBonus && (
        <div
          className="bonus-details-overlay"
          onClick={(event) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              closeDetails();
            }
          }}
        >

          <div
            className="bonus-details-modal"
            role="dialog"
            aria-modal="true"
          >

            {/* ===============================================
                MODAL HEADER
            =============================================== */}

            <div className="details-header">

              <div>

                <span>
                  MONTHLY BONUS
                </span>

                <h2>
                  {getMonthName(
                    selectedBonus.month,
                    selectedBonus.year
                  )}
                </h2>

              </div>

              <button
                type="button"
                className="details-close-btn"
                onClick={closeDetails}
                aria-label="Close details"
              >
                ×
              </button>

            </div>

            {/* ===============================================
                STATUS + AMOUNT
            =============================================== */}

            <div className="details-main">

              {/* BONUS AMOUNT */}

              <div className="details-main-item">

                <span>
                  BONUS AMOUNT
                </span>

                <strong>
                  {formatNumber(
                    selectedBonus.bonusAmount
                  )}{" "}
                  {selectedBonus.currency ||
                    "TZS"}
                </strong>

              </div>

              {/* STATUS */}

              <div className="details-main-item">

                <span>
                  STATUS
                </span>

                <div
                  className={getStatusClass(
                    selectedBonus.status
                  )}
                >
                  {getStatusLabel(
                    selectedBonus.status
                  )}
                </div>

              </div>

            </div>

            {/* ===============================================
                SCORE
            =============================================== */}

            <div className="details-section">

              <div className="details-section-title">
                PERFORMANCE
              </div>

              <div className="details-score">

                <div className="details-score-top">

                  <span>
                    PERFORMANCE SCORE
                  </span>

                  <strong>
                    {Number(
                      selectedBonus.score || 0
                    )}
                    %
                  </strong>

                </div>

                <div className="details-score-bar">

                  <div
                    className="details-score-progress"
                    style={{
                      width: `${Math.min(
                        Math.max(
                          Number(
                            selectedBonus.score ||
                              0
                          ),
                          0
                        ),
                        100
                      )}%`,
                    }}
                  />

                </div>

              </div>

            </div>

            {/* ===============================================
                PERFORMANCE DETAILS
            =============================================== */}

            <div className="details-section">

              <div className="details-section-title">
                PERFORMANCE DETAILS
              </div>

              <div className="details-grid">

                {/* LEVEL 1 */}

                <div className="details-stat">

                  <span>
                    LEVEL 1
                  </span>

                  <strong>
                    {Number(
                      selectedBonus.qualifiedLevel1Count ||
                        0
                    )}
                    /
                    {Number(
                      selectedBonus.level1Count ||
                        0
                    )}
                  </strong>

                </div>

                {/* DEPOSITS */}

                <div className="details-stat">

                  <span>
                    DEPOSITS
                  </span>

                  <strong>
                    {formatNumber(
                      selectedBonus.depositCount
                    )}
                  </strong>

                </div>

                {/* TOTAL DEPOSIT */}

                <div className="details-stat">

                  <span>
                    TOTAL DEPOSIT
                  </span>

                  <strong>
                    {formatNumber(
                      selectedBonus.totalDepositTZS
                    )}{" "}
                    <small>
                      TZS
                    </small>
                  </strong>

                </div>

                {/* PLAY DAYS */}

                <div className="details-stat">

                  <span>
                    PLAY DAYS
                  </span>

                  <strong>
                    {formatNumber(
                      selectedBonus.playDays
                    )}
                  </strong>

                </div>

                {/* BETS */}

                <div className="details-stat">

                  <span>
                    BETS
                  </span>

                  <strong>
                    {formatNumber(
                      selectedBonus.betCount
                    )}
                  </strong>

                </div>

                {/* MONTH */}

                <div className="details-stat">

                  <span>
                    MONTH
                  </span>

                  <strong>
                    {selectedBonus.monthKey ||
                      "-"}
                  </strong>

                </div>

              </div>

            </div>

            {/* ===============================================
                APPROVED DATE
            =============================================== */}

            {selectedBonus.approvedAt && (
              <div className="details-date">

                <span>
                  APPROVED DATE
                </span>

                <strong>
                  {formatDateTime(
                    selectedBonus.approvedAt
                  )}
                </strong>

              </div>
            )}

            {/* ===============================================
                ACCOUNT
            =============================================== */}

            <div className="details-user">

              <span>
                ACCOUNT
              </span>

              <strong>
                {selectedBonus.username ||
                  username}
              </strong>

            </div>

            {/* ===============================================
                SOURCE
            =============================================== */}

            {selectedBonus.source && (
              <div className="details-source">
                Source:{" "}
                {selectedBonus.source}
              </div>
            )}

          </div>

        </div>
      )}

    </div>
  );
};

export default UserMonthlyBonusHistory;
